import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Tabs, TabList, TabPanels, Tab, TabPanel, Icon, HStack, Badge, Table, Thead, Tbody, Tr, Th, Td, Button, List, ListItem, ListIcon, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Link } from "@chakra-ui/react";
import { FaChartLine, FaDollarSign, FaUsers, FaKey, FaPercentage, FaCheckCircle, FaArrowRight, FaPhone, FaHandshake, FaBuilding, FaExclamationTriangle, FaQuestionCircle, FaExternalLinkAlt, FaSyncAlt, FaStar } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const GRAPHITE = "#141C49";
const SAND = "#C2A878";
const SAND_LIGHT = "#F5F0E8";

const FinanceServicesInvestment: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/services/investment"
        fallbackTitle="Investment Property Finance - One Finance Australia"
        fallbackDescription="Build wealth through property investment. Access competitive rates, tax benefits, and strategic finance solutions for your portfolio."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <HStack spacing={12} align="center" flexDir={{ base: "column", lg: "row" }}>
            <VStack align={{ base: "center", lg: "start" }} spacing={4} flex={1} textAlign={{ base: "center", lg: "left" }}>
              <Badge bg={SAND} color={NAVY} fontSize="md" px={4} py={2} borderRadius="full">
                Build Wealth Through Property
              </Badge>
              <Heading size="2xl" fontWeight="bold">
                Investment Property Finance
              </Heading>
              <Text fontSize="lg" opacity={0.9}>
                Use your equity smarter, reduce your tax, and build a property portfolio with finance structured around your goals.
              </Text>
            </VStack>
            <SimpleGrid columns={2} spacing={6}>
              <Box bg="rgba(255,255,255,0.1)" p={6} borderRadius="xl" textAlign="center">
                <Text fontSize="3xl" fontWeight="bold">Up to 90%</Text>
                <Text fontSize="sm">LVR for Investment</Text>
              </Box>
              <Box bg="rgba(255,255,255,0.1)" p={6} borderRadius="xl" textAlign="center">
                <Text fontSize="3xl" fontWeight="bold">100%</Text>
                <Text fontSize="sm">Tax Deductible on New Builds</Text>
              </Box>
            </SimpleGrid>
          </HStack>
        </Container>
      </Box>

      {/* Is Investing Right For Me */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} alignItems="center">
            <VStack align="flex-start" spacing={4}>
              <HStack>
                <Icon as={FaQuestionCircle} boxSize={8} color={SAND} />
                <Heading size="lg" color={NAVY}>Is an Investment Property Right for Me?</Heading>
              </HStack>
              <Text color="gray.600" lineHeight="tall">
                Before you dive into rates and structures, it's worth checking the fundamentals. Here's what's worth thinking through first.
              </Text>
              <Button
                as={RouterLink}
                to="/finance/contact"
                variant="link"
                color={NAVY}
                fontWeight="600"
                rightIcon={<FaArrowRight />}
              >
                Not sure? Book a free, no-obligation chat
              </Button>
            </VStack>
            <List spacing={4}>
              {[
                { q: "Do you have usable equity or a deposit?", a: "Most investment loans require either equity in an existing property or a cash deposit of around 10–20%, plus funds for stamp duty and costs." },
                { q: "Are you in a tax bracket where negative gearing provides meaningful benefit?", a: "Negative gearing reduces your taxable income — the higher your marginal tax rate, the more impactful it tends to be. It's worth modelling this with your accountant before you buy." },
                { q: "Are you considering new builds vs existing property given the 2026 reforms?", a: "Proposed changes to negative gearing and CGT (see below) treat new builds differently from existing property purchased after 12 May 2026 — this may affect which option suits your strategy." }
              ].map((item) => (
                <ListItem key={item.q} display="flex" alignItems="flex-start" bg="white" p={5} borderRadius="lg" border="1px solid" borderColor="gray.200">
                  <ListIcon as={FaCheckCircle} color={SAND} mt={1} flexShrink={0} />
                  <Box>
                    <Text fontWeight="600" color={NAVY} fontSize="sm">{item.q}</Text>
                    <Text fontSize="sm" color="gray.600" mt={1}>{item.a}</Text>
                  </Box>
                </ListItem>
              ))}
            </List>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Tabbed Strategy Sections */}
      <Container maxW="container.xl" py={20}>
        <Tabs variant="soft-rounded" colorScheme="gray">
          <TabList mb={8} flexWrap="wrap">
            <Tab>Investment Strategies</Tab>
            <Tab>Tax Benefits</Tab>
            <Tab>Loan Structures</Tab>
            <Tab>Portfolio Growth</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                <Box p={8} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                  <Icon as={FaChartLine} boxSize={10} color={SAND} mb={4} />
                  <Heading size="md" mb={3}>Capital Growth Strategy</Heading>
                  <Text color="gray.700">Focus on high-growth areas with strong demographics and infrastructure. Target 7–10% annual capital appreciation for long-term wealth creation.</Text>
                </Box>
                <Box p={8} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                  <Icon as={FaDollarSign} boxSize={10} color={SAND} mb={4} />
                  <Heading size="md" mb={3}>Cash Flow Strategy</Heading>
                  <Text color="gray.700">Positive cash flow properties that generate rental income exceeding loan repayments. Ideal for passive income and funding further purchases.</Text>
                </Box>
                <Box p={8} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                  <Icon as={FaKey} boxSize={10} color={GRAPHITE} mb={4} />
                  <Heading size="md" mb={3}>Rentvesting</Heading>
                  <Text color="gray.700">Live where you want while investing where growth is strongest. Maintain lifestyle while building wealth in high-potential markets.</Text>
                </Box>
                <Box p={8} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                  <Icon as={FaUsers} boxSize={10} color={GRAPHITE} mb={4} />
                  <Heading size="md" mb={3}>Portfolio Diversification</Heading>
                  <Text color="gray.700">Spread risk across multiple properties, locations, and property types. Balance growth and income across your investment portfolio.</Text>
                </Box>
              </SimpleGrid>
            </TabPanel>

            <TabPanel>
              <VStack spacing={8} align="stretch">
                <Box p={8} bg={SAND_LIGHT} border="2px solid" borderColor={SAND} borderRadius="xl">
                  <Heading size="lg" mb={6} color={NAVY}>Investment Property Tax Advantages</Heading>
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Tax Benefit</Th>
                        <Th>Description</Th>
                        <Th isNumeric>Potential Savings</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td fontWeight="bold">Negative Gearing</Td>
                        <Td>Offset rental losses against your other income, reducing your overall tax bill</Td>
                        <Td isNumeric>$5,000+ /year*</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">Interest Deductions</Td>
                        <Td>Loan interest (not principal) is fully tax deductible while the property earns income</Td>
                        <Td isNumeric>$15,000+ /year*</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">Depreciation</Td>
                        <Td>Claim building (Div 43) and asset (Div 40) depreciation — including non-cash deductions</Td>
                        <Td isNumeric>$10,000+ /year*</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">Property Management</Td>
                        <Td>Deduct property manager fees, maintenance, rates, and insurance</Td>
                        <Td isNumeric>$3,000+ /year*</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">CGT 50% Discount</Td>
                        <Td>50% CGT discount for assets held 12+ months — applies to properties purchased before 12 May 2026</Td>
                        <Td isNumeric>Subject to purchase date&dagger;</Td>
                      </Tr>
                    </Tbody>
                  </Table>
                  <Text fontSize="xs" color="gray.500" mt={4}>
                    * Potential savings vary based on your marginal tax rate, loan size, and property type. This is general information only — speak with a qualified accountant for advice specific to your situation.
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    &dagger; See the tax reform notice below.
                  </Text>
                </Box>

                <Box p={8} bg="orange.50" border="2px solid" borderColor="orange.300" borderRadius="xl">
                  <HStack mb={4} spacing={3} align="flex-start">
                    <Icon as={FaExclamationTriangle} boxSize={6} color="orange.500" mt={1} />
                    <Heading size="md" color={NAVY}>Important: 2026 Federal Budget Changes</Heading>
                  </HStack>
                  <Text color="gray.700" mb={4}>
                    The Australian Government announced reforms to negative gearing and CGT on 12 May 2026, intended to take effect from 1 July 2027 (subject to legislation passing).
                  </Text>
                  <Text fontWeight="600" color={NAVY} mb={2}>What&apos;s changing:</Text>
                  <List spacing={2} mb={4}>
                    <ListItem display="flex" alignItems="flex-start">
                      <ListIcon as={FaExclamationTriangle} color="orange.500" mt={1} flexShrink={0} />
                      <Text color="gray.700" fontSize="sm">Negative gearing will be limited to new builds for properties purchased after 12 May 2026</Text>
                    </ListItem>
                    <ListItem display="flex" alignItems="flex-start">
                      <ListIcon as={FaExclamationTriangle} color="orange.500" mt={1} flexShrink={0} />
                      <Text color="gray.700" fontSize="sm">CGT 50% discount will be replaced with cost base indexation and a 30% minimum tax rate on gains accruing after 1 July 2027</Text>
                    </ListItem>
                  </List>
                  <Text fontWeight="600" color={NAVY} mb={2}>What&apos;s protected:</Text>
                  <List spacing={2} mb={4}>
                    <ListItem display="flex" alignItems="flex-start">
                      <ListIcon as={FaCheckCircle} color="green.500" mt={1} flexShrink={0} />
                      <Text color="gray.700" fontSize="sm">Properties already held at 7:30pm AEST on 12 May 2026 retain existing negative gearing entitlements</Text>
                    </ListItem>
                    <ListItem display="flex" alignItems="flex-start">
                      <ListIcon as={FaCheckCircle} color="green.500" mt={1} flexShrink={0} />
                      <Text color="gray.700" fontSize="sm">CGT reforms only apply to gains that accrue after 1 July 2027</Text>
                    </ListItem>
                    <ListItem display="flex" alignItems="flex-start">
                      <ListIcon as={FaCheckCircle} color="green.500" mt={1} flexShrink={0} />
                      <Text color="gray.700" fontSize="sm">New builds remain eligible for both negative gearing and the 50% CGT discount (or indexation)</Text>
                    </ListItem>
                  </List>
                  <Text color="gray.700" fontSize="sm" mb={3}>
                    These changes are not yet law. We recommend speaking with your accountant about how they may affect your strategy.
                  </Text>
                  <Link
                    href="https://www.ato.gov.au/about-ato/new-legislation/in-detail/individuals/tax-reform-boosting-home-ownership-reforming-negative-gearing-and-capital-gains-tax"
                    isExternal
                    color={NAVY}
                    fontWeight="600"
                    fontSize="sm"
                  >
                    ATO information <Icon as={FaExternalLinkAlt} boxSize={3} ml={1} />
                  </Link>
                </Box>
              </VStack>
            </TabPanel>

            <TabPanel>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
                <Box p={6} bg="white" border="2px solid" borderColor={SAND} borderRadius="xl">
                  <Badge bg={SAND} color={NAVY} mb={3}>Most Popular</Badge>
                  <Heading size="md" mb={3}>Interest Only</Heading>
                  <Text color="gray.600" mb={4}>Lower repayments, maximise cash flow and tax deductions during investment phase.</Text>
                  <Text fontSize="sm" color="gray.500">Typical LVR: 80–90%</Text>
                </Box>
                <Box p={6} bg="white" border="2px solid" borderColor={GRAPHITE} borderRadius="xl">
                  <Badge bg={GRAPHITE} color="white" mb={3}>Builds Equity</Badge>
                  <Heading size="md" mb={3}>Principal &amp; Interest</Heading>
                  <Text color="gray.600" mb={4}>Pay down the loan balance as well as interest. Higher repayments now, but less debt and interest over the life of the loan.</Text>
                  <Text fontSize="sm" color="gray.500">Typical LVR: 80–90%</Text>
                </Box>
                <Box p={6} bg="white" border="2px solid" borderColor={GRAPHITE} borderRadius="xl">
                  <Badge bg={GRAPHITE} color="white" mb={3}>Advanced</Badge>
                  <Heading size="md" mb={3}>Line of Credit</Heading>
                  <Text color="gray.600" mb={4}>Access equity for further purchases. Interest-only payments on drawn amounts.</Text>
                  <Text fontSize="sm" color="gray.500">Typical LVR: 60–80%</Text>
                </Box>
                <Box p={6} bg="white" border="2px solid" borderColor={SAND} borderRadius="xl">
                  <HStack mb={3}>
                    <Icon as={FaPercentage} color={SAND} />
                    <Badge bg={SAND} color={NAVY}>Certainty</Badge>
                  </HStack>
                  <Heading size="md" mb={3}>Fixed Rate</Heading>
                  <Text color="gray.600" mb={4}>Lock in your rate for 1–5 years for repayment certainty. Usually limits extra repayments, redraw, and offset functionality.</Text>
                  <Text fontSize="sm" color="gray.500">Term: 1–5 years</Text>
                </Box>
                <Box p={6} bg="white" border="2px solid" borderColor={GRAPHITE} borderRadius="xl">
                  <HStack mb={3}>
                    <Icon as={FaSyncAlt} color={GRAPHITE} />
                    <Badge bg={GRAPHITE} color="white">Flexible</Badge>
                  </HStack>
                  <Heading size="md" mb={3}>Variable Rate</Heading>
                  <Text color="gray.600" mb={4}>Rate moves with the market. Typically allows unlimited extra repayments, redraw, and offset accounts for active portfolio management.</Text>
                  <Text fontSize="sm" color="gray.500">Term: Ongoing</Text>
                </Box>
                <Box p={6} bg="white" border="2px solid" borderColor={GRAPHITE} borderRadius="xl">
                  <Badge bg={GRAPHITE} color="white" mb={3}>Flexible</Badge>
                  <Heading size="md" mb={3}>Offset &amp; Redraw</Heading>
                  <Text color="gray.600" mb={4}>Link savings to reduce interest while maintaining deductions on the full loan amount. Keep investment and personal funds in separate accounts — mixing them can blur what's deductible.</Text>
                  <Text fontSize="sm" color="gray.500">Typical LVR: 70–80%</Text>
                </Box>
              </SimpleGrid>

              <Box p={6} bg="orange.50" border="1px solid" borderColor="orange.300" borderRadius="xl">
                <HStack mb={2} spacing={3} align="flex-start">
                  <Icon as={FaExclamationTriangle} boxSize={5} color="orange.500" mt={1} />
                  <Heading size="sm" color={NAVY}>A Note on Risk: Cross-Collateralisation</Heading>
                </HStack>
                <Text color="gray.700" fontSize="sm">
                  Using equity in one property to secure another can simplify borrowing, but it ties your properties together as security for the same lender. If one loan runs into trouble, the lender may have a claim across your whole portfolio. Where your structure allows, we generally recommend separate securities — and sometimes separate lenders — to keep your properties protected individually.
                </Text>
              </Box>
            </TabPanel>

            <TabPanel>
              <Box p={10} bg="gray.50" borderRadius="xl">
                <Heading size="lg" mb={6} textAlign="center">Portfolio Growth Timeline</Heading>
                <VStack spacing={6} align="stretch">
                  {[
                    { year: "Year 1–2", title: "Foundation", desc: "Purchase first investment property. Establish rental income and tax benefits." },
                    { year: "Year 3–5", title: "Leverage Equity", desc: "Use capital growth to access equity. Purchase second property using increased borrowing capacity." },
                    { year: "Year 6–10", title: "Portfolio Expansion", desc: "Continue leveraging equity across portfolio. Aim for 3–5 properties with diversified locations." },
                    { year: "Year 10+", title: "Wealth Optimisation", desc: "Transition to principal & interest loans. Reduce debt while maintaining portfolio value." }
                  ].map((phase, idx) => (
                    <HStack key={idx} spacing={6} p={6} bg="white" borderRadius="lg" shadow="sm">
                      <Box minW="100px">
                        <Badge bg={SAND} color={NAVY} fontSize="sm">{phase.year}</Badge>
                      </Box>
                      <Box flex={1}>
                        <Heading size="sm" mb={2}>{phase.title}</Heading>
                        <Text fontSize="sm" color="gray.600">{phase.desc}</Text>
                      </Box>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>

      {/* SMSF Section */}
      <Box bg={SAND_LIGHT} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Badge bg={SAND} color={NAVY} fontSize="sm" px={4} py={1} borderRadius="full">
              Super &amp; SMSF
            </Badge>
            <Heading size="xl" color={NAVY}>Grow Your Super Through Property</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Your superannuation doesn't have to sit in a managed fund. With the right structure, it can work harder — in bricks and mortar.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            {/* SMSF Loans */}
            <Box bg="white" borderRadius="2xl" p={10} border="1px solid" borderColor="gray.200" shadow="sm">
              <HStack mb={5} spacing={4}>
                <Icon as={FaBuilding} boxSize={10} color={SAND} />
                <Badge bg={SAND} color={NAVY} fontSize="sm" px={3} py={1} borderRadius="full">SMSF Loans</Badge>
              </HStack>
              <Heading size="md" mb={4} color={NAVY}>Lending for Your Self-Managed Super Fund</Heading>
              <Text color="gray.600" lineHeight="tall" mb={5}>
                Not every lender offers SMSF loans — and the structure has to be right before finance can be arranged. We work with specialist lenders who understand the SMSF borrowing rules, so your loan fits correctly within the fund's trust structure from day one.
              </Text>
              <List spacing={3}>
                {[
                  "Access to specialist SMSF lenders",
                  "Residential and commercial property finance",
                  "Limited recourse borrowing arrangements (LRBAs)",
                  "Bare trust structure guidance"
                ].map((item) => (
                  <ListItem key={item} display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color={SAND} mt={1} flexShrink={0} />
                    <Text color="gray.700" fontSize="sm" dangerouslySetInnerHTML={{ __html: item }} />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* SMSF Setup */}
            <Box bg="white" borderRadius="2xl" p={10} border="2px solid" borderColor={SAND} shadow="md" position="relative">
              <Badge
                bg={SAND}
                color={NAVY}
                position="absolute"
                top={-3}
                left={8}
                fontSize="xs"
                px={3}
                py={1}
                borderRadius="full"
              >
                Via Trusted Partner
              </Badge>
              <HStack mb={5} spacing={4} mt={2}>
                <Icon as={FaHandshake} boxSize={10} color={SAND} />
                <Badge bg={NAVY} color="white" fontSize="sm" px={3} py={1} borderRadius="full">SMSF Setup</Badge>
              </HStack>
              <Heading size="md" mb={4} color={NAVY}>Diversify Your Super — We Guide You Every Step</Heading>
              <Text color="gray.600" lineHeight="tall" mb={4}>
                Setting up an SMSF is a big decision with real complexity behind it. We partner with a licensed SMSF specialist who handles the setup, structure, and ongoing compliance — so you're never trying to piece it together on your own.
              </Text>
              <Text color="gray.600" lineHeight="tall" mb={5}>
                Our role is to coordinate the full picture: helping you understand whether an SMSF suits your situation, connecting you with our specialist partner, and arranging the lending once you're ready to act. One conversation with us sets the whole process in motion.
              </Text>
              <Box bg={SAND_LIGHT} borderRadius="lg" p={5} border="1px solid" borderColor={SAND}>
                <Text fontSize="sm" color={NAVY} fontWeight="600" mb={2}>Important note</Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  SMSF setup and financial advice is provided by our licensed specialist partner. One Group Finance arranges the lending. We work together to make sure the structure and the loan fit correctly from the start.
                </Text>
              </Box>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Trust & Social Proof */}
      <Box bg="white" py={20}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={6} mb={12}>
            {[
              { stat: "$72M", label: "Settled Since 2003" },
              { stat: "187", label: "Loans Written" },
              { stat: "151", label: "Clients Helped" },
              { stat: "40+", label: "Lender Panel Access" },
              { stat: "4 days", label: "Average Pre-Approval" },
              { stat: "14–21 days", label: "Average Full Approval" }
            ].map((item) => (
              <Box key={item.label} p={6} bg="gray.50" borderRadius="xl" textAlign="center" border="1px solid" borderColor="gray.100">
                <Text fontSize="2xl" fontWeight="bold" color={NAVY}>{item.stat}</Text>
                <Text fontSize="sm" color="gray.600">{item.label}</Text>
              </Box>
            ))}
          </SimpleGrid>
          <Box maxW="2xl" mx="auto" bg={SAND_LIGHT} borderRadius="xl" p={8} border="1px solid" borderColor={SAND} textAlign="center">
            <HStack justify="center" spacing={1} mb={3}>
              {[...Array(5)].map((_, i) => (
                <Icon as={FaStar} key={i} color={SAND} />
              ))}
            </HStack>
            <Text fontStyle="italic" color="gray.700" mb={4}>
              "One Group helped us structure our second investment loan so we kept full deductibility while accessing equity from our home. They made the SMSF side simple too."
            </Text>
            <Text fontWeight="600" color={NAVY} fontSize="sm">David &amp; Priya, Melbourne</Text>
          </Box>
        </Container>
      </Box>

      {/* FAQ */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Icon as={FaQuestionCircle} boxSize={10} color={SAND} />
            <Heading size="xl" color={NAVY}>Frequently Asked Questions</Heading>
          </VStack>
          <Box maxW="3xl" mx="auto">
            <Accordion allowMultiple>
              {[
                { q: "Can I use equity in my home to buy an investment property?", a: "Yes — this is one of the most common ways investors fund a deposit. A lender will assess the usable equity in your home (generally up to 80% of its value, minus what you owe) and can use this instead of, or alongside, cash savings." },
                { q: "How is an investment loan different from a home loan?", a: "Investment loans are assessed against rental income as well as your personal income, often carry slightly different rates, and are commonly structured as interest-only to maximise cash flow and tax deductions during the investment phase." },
                { q: "What deposit do I need for an investment property?", a: "Most lenders ask for at least 10–20% of the purchase price, plus costs such as stamp duty and legal fees. Some lenders offer up to 90% LVR for investment loans with lenders mortgage insurance (LMI), though this increases the overall cost of the loan." },
                { q: "How does negative gearing actually work with my tax return?", a: "If your property's expenses (interest, management fees, depreciation, etc.) exceed your rental income, the shortfall can generally be deducted against your other taxable income, reducing your overall tax bill. The benefit depends on your marginal tax rate — speak with your accountant for advice specific to you." },
                { q: "What's changing with negative gearing from 2027?", a: "Proposed reforms would limit negative gearing to new builds for properties purchased after 12 May 2026, with existing investment properties retaining current entitlements. These changes are not yet law — see the tax reform notice above for details." }
              ].map((faq, i) => (
                <AccordionItem key={i} border="1px" borderColor="gray.200" borderRadius="lg" mb={4} bg="white">
                  <AccordionButton py={5} _expanded={{ bg: SAND_LIGHT }}>
                    <Box flex={1} textAlign="left">
                      <Heading size="sm" color={NAVY}>{faq.q}</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={6}>
                    <Text color="gray.600" fontSize="sm" lineHeight="tall">{faq.a}</Text>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box bg={GRAPHITE} color="white" py={16}>
        <Container maxW="container.xl" textAlign="center">
          <Heading size="xl" mb={4}>Ready to Build Your Portfolio?</Heading>
          <Text fontSize="lg" mb={8} color="gray.300">
            Whether it's your first investment property or you're growing an existing one — we'll help you find the right finance structure and make it happen.
          </Text>
          <HStack justify="center" spacing={4} flexWrap="wrap">
            <Button
              as={RouterLink}
              to="/finance/contact"
              size="lg"
              bg={SAND}
              color={NAVY}
              fontWeight="bold"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#a8905f", color: "white" }}
              transition="all 0.2s"
            >
              Book a Free Consultation
            </Button>
            <Button
              size="lg"
              variant="outline"
              borderColor="white"
              color="white"
              leftIcon={<FaPhone />}
              _hover={{ bg: "rgba(255,255,255,0.1)" }}
              transition="all 0.2s"
            >
              1300 536 159
            </Button>
          </HStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesInvestment;
